import { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { ChevronDown } from "lucide-react";
import { motion } from "framer-motion";

/**
 * SearchableSelect - A premium searchable input with dropdown suggestions
 * Allows both free typing and selection from options
 * Uses portal for dropdown to escape overflow-hidden containers
 * Variants: "default" | "glass" | "light"
 */
const SearchableSelect = ({
  value,
  onChange,
  options,
  placeholder = "Select...",
  icon: Icon,
  suffix,
  className = "",
  inputClassName = "",
  variant = "default", // "default" | "glass" | "light"
  "data-testid": testId,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [filteredOptions, setFilteredOptions] = useState(options);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  // Find the label for the current value
  const selectedOption = options.find((opt) => opt.value === value);

  // Update filtered options when input changes
  useEffect(() => {
    if (inputValue) {
      const filtered = options.filter((opt) =>
        opt.label.toLowerCase().includes(inputValue.toLowerCase())
      );
      setFilteredOptions(filtered);
    } else {
      setFilteredOptions(options);
    }
  }, [inputValue, options]);

  // Calculate dropdown position
  const updateDropdownPosition = useCallback(() => {
    if (wrapperRef.current) {
      const rect = wrapperRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      });
    }
  }, []);

  // Update position when open
  useEffect(() => {
    if (isOpen) {
      updateDropdownPosition();
      window.addEventListener("scroll", updateDropdownPosition, true);
      window.addEventListener("resize", updateDropdownPosition);
      return () => {
        window.removeEventListener("scroll", updateDropdownPosition, true);
        window.removeEventListener("resize", updateDropdownPosition);
      };
    }
  }, [isOpen, updateDropdownPosition]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const isInsideWrapper = wrapperRef.current && wrapperRef.current.contains(event.target);
      const isInsideDropdown = dropdownRef.current && dropdownRef.current.contains(event.target);
      
      if (!isInsideWrapper && !isInsideDropdown) {
        setIsOpen(false);
        if (selectedOption) {
          setInputValue("");
        }
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen, selectedOption]);

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
    if (!isOpen) setIsOpen(true);
  };

  const handleInputFocus = () => {
    setIsOpen(true);
    setInputValue("");
    updateDropdownPosition();
  };

  const handleOptionSelect = (option) => {
    onChange(option.value);
    setInputValue("");
    setIsOpen(false);
    inputRef.current?.blur();
  };

  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      setIsOpen(false);
      setInputValue("");
      inputRef.current?.blur();
    }
    if (e.key === "Enter" && filteredOptions.length > 0) {
      e.preventDefault();
      handleOptionSelect(filteredOptions[0]);
    }
  };

  // Display value: show input when typing, otherwise show selected label or placeholder
  const displayValue = isOpen ? inputValue : (selectedOption?.label || "");

  const isGlass = variant === "glass";
  const isLight = variant === "light";
  const isActive = isOpen;

  const iconClass = isGlass
    ? "text-white/60"
    : isLight
      ? isActive
        ? "text-[#1A1A1A]/55"
        : "text-[#666660]/80"
      : "text-[#B0B0B0] group-hover:text-[#888]";

  const chevronClass = isGlass
    ? "text-white/60"
    : isLight
      ? "text-[#666660]"
      : "text-[#B0B0B0]";

  const placeholderClass = isGlass
    ? "placeholder:text-white/50"
    : isLight
      ? "placeholder:text-[#666660]"
      : "placeholder:text-[#999]";

  const suffixClass = isGlass
    ? "text-white/50"
    : isLight
      ? "text-[#666660]"
      : "text-[#A0A0A0]";

  return (
    <div
      ref={wrapperRef}
      className={`relative transition-colors duration-200 ${
        isLight && isActive
          ? "bg-white -mx-1 px-1 py-0.5 shadow-sm border border-[#E8E6E1]"
          : ""
      } ${className}`}
    >
      <div className="flex items-center gap-2 cursor-pointer">
        {Icon && (
          <Icon
            size={15}
            className={`flex-shrink-0 transition-colors ${iconClass}`}
            strokeWidth={1.5}
            aria-hidden="true"
          />
        )}
        <div className="flex-1 relative">
          <input
            ref={inputRef}
            type="text"
            value={displayValue}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            aria-label={placeholder}
            className={`w-full bg-transparent focus:outline-none focus-visible:ring-1 focus-visible:ring-[#1A1A1A]/25 cursor-pointer min-h-[28px] ${placeholderClass} ${inputClassName}`}
            data-testid={testId}
          />
          <ChevronDown
            size={14}
            className={`absolute right-0 top-1/2 -translate-y-1/2 pointer-events-none transition-transform duration-200 ${isOpen ? "rotate-180" : ""} ${chevronClass}`}
            strokeWidth={1.5}
            aria-hidden="true"
          />
        </div>
        {suffix && (
          <span className={`text-[11px] uppercase tracking-wide flex-shrink-0 ${suffixClass}`}>
            {suffix}
          </span>
        )}
      </div>

      {/* Dropdown rendered via portal to escape overflow-hidden containers */}
      {isOpen && typeof document !== "undefined" && createPortal(
        <motion.div
          ref={dropdownRef}
          initial={{ opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.15 }}
          className="bg-white shadow-lg border border-[#E8E6E1] max-h-[240px] overflow-y-auto"
          style={{
            position: "fixed",
            top: dropdownPosition.top,
            left: dropdownPosition.left,
            width: dropdownPosition.width > 0 ? dropdownPosition.width : 200,
            zIndex: 9999,
          }}
          data-testid={`${testId}-dropdown`}
        >
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option, index) => (
              <button
                key={option.value || `opt-${index}`}
                type="button"
                onClick={() => handleOptionSelect(option)}
                className={`w-full text-left px-4 py-3 text-[13px] font-light transition-colors hover:bg-byron-bg ${
                  option.value === value ? "bg-byron-bg text-[#1A1A1A]" : "text-[#444]"
                }`}
                data-testid={`${testId}-option-${option.value || index}`}
              >
                {option.label}
              </button>
            ))
          ) : (
            <div className="px-4 py-3 text-[13px] text-[#999] font-light">
              No results found
            </div>
          )}
        </motion.div>,
        document.body
      )}
    </div>
  );
};

export default SearchableSelect;
